import React, { useState } from "react";
import { useAssetContext } from "../../DataContext/AssetContext";

type AlignmentOption = "beginning" | "industry" | "ending";

const OPTIONS: { value: AlignmentOption; label: string }[] = [
  { value: "beginning", label: "Beginning" },
  { value: "industry", label: "Industry" },
  { value: "ending", label: "Ending" },
];

const TOOLTIP_CONTENT: Record<AlignmentOption, string> = {
  beginning: "Hour Beginning: each timestamp represents the start of a period — the data point at 09:00 covers the interval from 09:00 to 09:05 (or 09:15 / 10:00 depending on granularity).",
  industry:
    "Industry (Raw): timestamps are returned exactly as stored in the database with no shifting applied.",
  ending:
    "Hour Ending: each timestamp represents the end of a period — the data point at 09:00 covers the interval from 08:55 (or 08:45 / 08:00 depending on granularity) to 09:00.",
};

const IntervalAlignmentToggle: React.FC = () => {
  const { intervalAlignment, setIntervalAlignment } = useAssetContext();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <div className="w-full">
      <div className="flex items-center gap-1 mb-1">
        <label className="block text-md font-medium text-gray-700">
          Interval Alignment
        </label>
        <div className="relative">
          <button
            type="button"
            className="w-5 h-5 rounded-full bg-gray-300 text-gray-700 text-xs font-bold flex items-center justify-center hover:bg-gray-400 focus:outline-none"
            onClick={() => setTooltipOpen((prev) => !prev)}
            onBlur={() => setTooltipOpen(false)}
            aria-label="Interval alignment help"
          >
            ?
          </button>
          {tooltipOpen && (
            <div className="absolute right-0 top-full mt-2 z-50 w-[min(22rem,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-3 shadow-lg text-sm text-gray-700">
              <p className="font-semibold mb-2">Interval Alignment</p>
              <ul className="space-y-2">
                {OPTIONS.map((opt) => (
                  <li key={opt.value}>
                    <span className="font-medium">{opt.label}:</span>{" "}
                    {TOOLTIP_CONTENT[opt.value]}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="flex w-full h-[38px] rounded-lg border border-gray-300 bg-gray-100 p-0.5">
        {OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setIntervalAlignment(opt.value)}
            className={`flex-1 flex items-center justify-center text-sm rounded-md transition-all duration-200 font-medium ${
              intervalAlignment === opt.value
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default IntervalAlignmentToggle;
